import { notifyError } from "@/lib/notify-error";
import { useState, type ComponentPropsWithoutRef, type ComponentType } from "react";
import { useLocation } from "@tanstack/react-router";
import { useAuth } from "@workos-inc/authkit-react";
import { useQuery } from "@tanstack/react-query";
import { convexQuery } from "@convex-dev/react-query";
import { useMutation } from "convex/react";
import {
  Building2,
  ChevronDown,
  CircleQuestionMark,
  FileText,
  Gauge,
  LayoutDashboard,
  ListChecks,
  LogOut,
  Mail,
  Menu,
  MessageSquareMore,
  MapPinned,
  PanelTop,
  ScanSearch,
  Settings,
  Shield,
  Users,
  Video,
  Wrench,
} from "lucide-react";

import { api } from "@constructionautomation/backend";
import { useTheme, type Theme } from "@/lib/theme";
import { useAiDebugMode } from "@/features/takeoff-pilot/use-ai-debug-mode";
import { useAppNavigate } from "@/lib/use-app-navigate";
import { cn } from "@/lib/utils";
import { AppLink } from "@/components/app-link";
import { ProjectSwitcher } from "@/components/projects";
import { CountBadge } from "@/components/semantic-badges";
import { Button } from "@/components/ui/button";
import { IconButton } from "@/components/ui/icon-button";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useUserProfile } from "@/components/auth";
import { FeedbackDialog } from "@/components/feedback/feedback-dialog";
import { useViewerCapabilities } from "@/lib/capabilities";
import { ViewOnlyIndicator } from "@/features/onboarding/view-only-indicator";

interface TopNavProps {
  className?: string;
}
const AI_TAKEOFF_ENABLED = import.meta.env.VITE_TAKEOFF_AI_ENABLED === "true";

const themeOptions: Array<{ id: Theme; label: string }> = [
  { id: "office", label: "Office" },
  { id: "blueprint", label: "Blueprint" },
  { id: "construction", label: "Construction" },
  { id: "field", label: "Field" },
  { id: "duck-hunt", label: "Duck Hunt" },
];

interface ToolNavItem {
  label: string;
  to: string;
  icon: ComponentType<{ className?: string }>;
  /** Route prefixes that mark this tool active (exact match or any nested path). */
  pathPrefixes: string[];
}

const TOOL_NAV_ITEMS: ToolNavItem[] = [
  { label: "Documents", to: "/project/documents", icon: FileText, pathPrefixes: ["/project/documents"] },
  { label: "SpecPilot", to: "/specPilot", icon: LayoutDashboard, pathPrefixes: ["/specPilot"] },
  { label: "TakeoffPilot", to: "/takeoffPilot", icon: ScanSearch, pathPrefixes: ["/takeoffPilot"] },
  { label: "FieldPilot", to: "/fieldPilot", icon: MapPinned, pathPrefixes: ["/fieldPilot"] },
  { label: "RevPilot", to: "/revPilot", icon: PanelTop, pathPrefixes: ["/revPilot", "/comparisons"] },
  { label: "CollabPilot", to: "/revCollab", icon: MessageSquareMore, pathPrefixes: ["/revCollab"] },
];

function findActiveTool(pathname: string) {
  return TOOL_NAV_ITEMS.find((item) =>
    item.pathPrefixes.some((prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`))
  );
}

const NAV_ICON_BUTTON_CLASSES =
  "text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground";

function MenuButton({
  label,
  icon: Icon,
  active = false,
  className,
  ...props
}: ComponentPropsWithoutRef<typeof Button> & {
  label: string;
  icon: ComponentType<{ className?: string }>;
  active?: boolean;
}) {
  return (
    <Button
      variant="ghost"
      className={cn(
        "gap-2 px-3 text-sm font-semibold text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground",
        active &&
          "bg-sidebar-primary text-sidebar-primary-foreground hover:bg-sidebar-primary/90 hover:text-sidebar-primary-foreground",
        className
      )}
      {...props}
    >
      <Icon className="size-4 shrink-0" />
      <span>{label}</span>
      <ChevronDown className="size-4 opacity-70" />
    </Button>
  );
}

function NavMenuItem({
  onSelect,
  icon: Icon,
  children,
}: {
  onSelect: () => void;
  icon?: ComponentType<{ className?: string }>;
  children: string;
}) {
  return (
    <DropdownMenuItem onSelect={onSelect}>
      {Icon ? <Icon className="size-4" /> : null}
      <span className="min-w-0 flex-1 truncate">{children}</span>
    </DropdownMenuItem>
  );
}

export function TopNav({ className }: TopNavProps) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [feedbackOpen, setFeedbackOpen] = useState(false);

  const { user, signOut } = useAuth();
  const { userProfile, isPlatformAdmin } = useUserProfile();
  const stopEmulation = useMutation(api.admin.organizations.stopEmulation);
  const { data: unreadCount } = useQuery(convexQuery(api.notifications.getUnreadCount, {}));
  const unread = unreadCount ?? 0;
  const { theme, setTheme } = useTheme();
  const aiDebug = useAiDebugMode();
  const navigate = useAppNavigate();
  const pathname = useLocation({ select: (location) => location.pathname });
  const canManageOrganization = useViewerCapabilities()?.canManageMembers ?? false;
  const organizationLogoUrl = userProfile?.organizationLogoUrl ?? "/favicon-32x32.png";
  const organizationName = userProfile?.organizationName ?? "Construction Cortex";

  const closeAllMenus = () => {
    setMobileMenuOpen(false);
  };

  const goTo = (to: string, title?: string, params?: Record<string, string>) => {
    navigate({ to, title, params });
    closeAllMenus();
  };

  // The Tools trigger names the tool you are in ("SpecPilot") and reads "Tools" elsewhere.
  const activeTool = findActiveTool(pathname);
  const isAdminRoute = pathname === "/admin" || pathname.startsWith("/admin/");
  const isInboxRoute = pathname === "/inbox";

  const handleSignOut = async () => {
    await signOut({ navigate: false });
    window.location.reload();
  };

  const handleStopEmulation = async () => {
    try {
      await stopEmulation({});
      goTo("/admin/organizations", "Organizations");
    } catch (error) {
      notifyError(error, error instanceof Error ? error.message : "Failed to leave support session");
    }
  };

  const renderProjectsSection = () => {
    return (
      <div className="flex items-center gap-2">
        <ProjectSwitcher />
        <ViewOnlyIndicator />
      </div>
    );
  };

  const renderToolsMenu = () => {
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <MenuButton
            label={activeTool?.label ?? "Tools"}
            aria-label={activeTool ? `Tools: ${activeTool.label}` : "Tools"}
            icon={activeTool?.icon ?? Wrench}
            active={activeTool !== undefined}
          />
        </DropdownMenuTrigger>
        <DropdownMenuContent className="w-56 border-2" align="start">
          <DropdownMenuLabel>Tools</DropdownMenuLabel>
          <DropdownMenuSeparator />
          {TOOL_NAV_ITEMS.map((item) => (
            <NavMenuItem key={item.to} icon={item.icon} onSelect={() => goTo(item.to, item.label)}>
              {item.label}
            </NavMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  };

  const renderAdminMenu = () => {
    if (!isPlatformAdmin) {
      return null;
    }

    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <MenuButton
            label="Admin"
            icon={Shield}
            active={isAdminRoute}
          />
        </DropdownMenuTrigger>
        <DropdownMenuContent className="w-56 border-2" align="start">
          <DropdownMenuLabel>Admin</DropdownMenuLabel>
          <DropdownMenuSeparator />
          <NavMenuItem icon={Gauge} onSelect={() => goTo("/admin", "Cost Dashboard")}>
            Cost Dashboard
          </NavMenuItem>
          <NavMenuItem icon={Building2} onSelect={() => goTo("/admin/organizations", "Organizations") }>
            Organizations
          </NavMenuItem>
          <NavMenuItem icon={Building2} onSelect={() => goTo("/admin/billing", "Billing") }>
            Billing
          </NavMenuItem>
          <NavMenuItem icon={Users} onSelect={() => goTo("/admin/roles", "Roles") }>
            Roles
          </NavMenuItem>
          <NavMenuItem icon={MessageSquareMore} onSelect={() => goTo("/admin/feedback", "Feedback") }>
            Feedback
          </NavMenuItem>
          <NavMenuItem icon={Video} onSelect={() => goTo("/admin/training", "Training Videos") }>
            Training Videos
          </NavMenuItem>
          {userProfile?.isEmulatingOrganization ? (
            <>
              <DropdownMenuSeparator />
              <NavMenuItem icon={LogOut} onSelect={() => void handleStopEmulation()}>
                {`Leave ${userProfile.organizationName ?? "organization"}`}
              </NavMenuItem>
            </>
          ) : null}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  };

  const renderSupportMenu = () => {
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <IconButton
            label="Support and feedback"
            icon={CircleQuestionMark}
            tooltipSide="bottom"
            className={NAV_ICON_BUTTON_CLASSES}
          />
        </DropdownMenuTrigger>
        <DropdownMenuContent className="w-56 border-2" align="end">
          <DropdownMenuLabel>Support</DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem onSelect={() => setFeedbackOpen(true)}>
            <MessageSquareMore className="size-4" />
            <span>Feedback</span>
          </DropdownMenuItem>
          <DropdownMenuItem disabled>
            <CircleQuestionMark className="size-4" />
            <span>Support</span>
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={() => goTo("/training", "Training Videos")}>
            <Video className="size-4" />
            <span>Training Videos</span>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    );
  };

  return (
    <header
      data-slot="top-nav"
      className={cn("border-b-2 border-sidebar-border bg-sidebar text-sidebar-foreground", className)}
    >
      <div className="flex h-16 items-center gap-2 px-4 lg:px-6">
        <AppLink to="/" className="flex shrink-0 items-center gap-2.5">
          <img
            src={organizationLogoUrl}
            alt={`${organizationName} Logo`}
            className="size-8 rounded object-contain"
            onError={(event) => {
              event.currentTarget.onerror = null;
              event.currentTarget.src = "/favicon-32x32.png";
            }}
          />
          {/* The wordmark is the first thing to go: it is the widest optional
              element and the logo still identifies the app without it. */}
          <span className="hidden text-base font-bold tracking-tight lg:inline">
            Construction Cortex
          </span>
        </AppLink>

        <div className="ms-6 hidden min-w-0 items-center gap-2 md:flex">
          {renderProjectsSection()}
          {renderToolsMenu()}
          {renderAdminMenu()}
        </div>

        <div className="ms-auto flex shrink-0 items-center gap-1">
          <IconButton
            label={unread > 0 ? `Inbox, ${unread} unread` : "Inbox"}
            tooltip="Inbox"
            tooltipSide="bottom"
            icon={Mail}
            className={cn(
              "relative",
              NAV_ICON_BUTTON_CLASSES,
              isInboxRoute &&
                "bg-sidebar-primary text-sidebar-primary-foreground hover:bg-sidebar-primary/90 hover:text-sidebar-primary-foreground"
            )}
            onClick={() => goTo("/inbox", "Inbox")}
          >
            {unread > 0 ? (
              <CountBadge
                value={unread}
                tone="primary"
                className="pointer-events-none absolute -top-1 -right-1"
              />
            ) : null}
          </IconButton>

          {renderSupportMenu()}

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <IconButton
                label="Settings"
                icon={Settings}
                tooltipSide="bottom"
                className={NAV_ICON_BUTTON_CLASSES}
              />
            </DropdownMenuTrigger>
            <DropdownMenuContent className="w-60 border-2" align="end">
              <DropdownMenuLabel>Settings</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuLabel className="text-xs text-muted-foreground">Theme</DropdownMenuLabel>
              <DropdownMenuRadioGroup
                value={theme}
                onValueChange={(value) => setTheme(value as Theme)}
              >
                {themeOptions.map((option) => (
                  <DropdownMenuRadioItem key={option.id} value={option.id}>
                    {option.label}
                  </DropdownMenuRadioItem>
                ))}
              </DropdownMenuRadioGroup>
              {aiDebug.available && AI_TAKEOFF_ENABLED ? (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuCheckboxItem
                    checked={aiDebug.enabled}
                    onCheckedChange={(checked) => aiDebug.setEnabled(Boolean(checked))}
                  >
                    AI Debug Mode
                  </DropdownMenuCheckboxItem>
                </>
              ) : null}
              {canManageOrganization ? (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onSelect={() => goTo("/organization", "Organization")}>
                    <Building2 className="size-4" />
                    <span>Organization</span>
                  </DropdownMenuItem>
                </>
              ) : null}
            </DropdownMenuContent>
          </DropdownMenu>

          <span className="hidden max-w-56 truncate px-2 text-xs font-semibold text-sidebar-foreground/80 xl:inline">
            {user?.email}
          </span>
          <Button
            variant="ghost"
            onClick={handleSignOut}
            className="gap-2 text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
            aria-label="Sign out"
          >
            <LogOut className="size-4" />
            <span className="hidden sm:inline">Logout</span>
          </Button>

          <IconButton
            label={mobileMenuOpen ? "Close navigation menu" : "Open navigation menu"}
            icon={Menu}
            tooltip={false}
            iconClassName="size-5"
            className={cn(NAV_ICON_BUTTON_CLASSES, "md:hidden")}
            aria-expanded={mobileMenuOpen}
            onClick={() => setMobileMenuOpen((prev) => !prev)}
          />
        </div>
      </div>

      {mobileMenuOpen ? (
        <div className="border-t-2 border-sidebar-border p-3 md:hidden">
          <div className="grid gap-2">
            <div className="flex flex-col gap-2">
              <ProjectSwitcher />
              <ViewOnlyIndicator className="self-start" />
              <Button
                variant="ghost"
                className="justify-start gap-2 text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
                onClick={() => goTo("/projects", "Projects")}
              >
                <ListChecks className="size-4" />
                All Projects
              </Button>
            </div>
            {renderToolsMenu()}
            {renderAdminMenu()}
          </div>
        </div>
      ) : null}
      <FeedbackDialog open={feedbackOpen} onOpenChange={setFeedbackOpen} />
    </header>
  );
}

/*
 * Usage Example:
 *
 * import { TopNav } from "@/components/layout";
 *
 * <TopNav />
 */
